/**
 * What the acceptance screen should show the moment the link is opened
 * (#1702 / #2318).
 *
 * Before this, the screen rendered the reservation and a "Kostenpflichtig
 * buchen" button for ANY hydrated order, and only learned it was refused after
 * the customer pressed it. So a guest whose booking had been cancelled — or
 * whose offer had lapsed days earlier — was shown a live-looking offer and
 * found out by clicking a button that says it costs money.
 *
 * The backend stays authoritative: it re-checks all of this on submit, and a
 * booking cancelled while the page sits open still fails there. This only
 * stops the screen from inviting a click it can already tell will be refused.
 *
 * Mirrors `evaluateAcceptance` in the backend, in the same order and for the
 * same reasons — concluded before anything else, and slot start before the
 * validity window, so a customer sees the fact they can check themselves.
 */
export type AcceptanceScreenState =
  | "acceptable"
  | "concluded"
  | "canceled"
  | "slot_started"
  | "offer_expired";

export type AcceptanceOrderView = {
  contractConcludedAt?: string | null;
  isCanceled?: boolean | null;
  acceptanceRequestedAt?: string | null;
  items?: { fromDate?: string }[];
};

const DAY_MS = 24 * 60 * 60 * 1000;

const time = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

export const acceptanceScreenState = (
  order: AcceptanceOrderView,
  /** The tenant's window, or null for "runs until the slot starts" (D8). */
  offerValidityDays: number | null,
  now: number,
): AcceptanceScreenState => {
  // Concluded first: a customer who accepted and re-opened the mail did
  // everything right and must never see a refusal.
  if (order.contractConcludedAt) return "concluded";
  if (order.isCanceled) return "canceled";

  const startsAt = time(order.items?.[0]?.fromDate);
  if (startsAt !== null && startsAt <= now) return "slot_started";

  const offeredAt = time(order.acceptanceRequestedAt);
  if (offerValidityDays !== null && offerValidityDays > 0 && offeredAt !== null) {
    if (offeredAt + offerValidityDays * DAY_MS <= now) return "offer_expired";
  }

  // Everything unknown reads as acceptable: a field absent because a response
  // predates it, or because the tenant is legacy, must not lock a customer out
  // of an offer that is in fact still open. The backend refuses if it is not.
  return "acceptable";
};
